import { useEffect, useMemo, useRef, useState } from "react";
import { api, fmtErr } from "../lib/api";
import { toast } from "sonner";
import { Sparkle } from "@phosphor-icons/react";
import { Modal, Field, ModalFooter } from "../pages/Contacts";

export function AIListBuilderDialog({ onClose, onSaved }) {
  const [brief, setBrief] = useState("");
  const [phase, setPhase] = useState("brief");
  const [job, setJob] = useState(null);
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [failedInfo, setFailedInfo] = useState({ count: 0, err: "" });
  const jobIdRef = useRef(null);
  const pollRef = useRef(null);

  const buildRows = (census, results, failedIdx, chunkSize) => {
    const reasons = {};
    for (const r of results) reasons[r.title.trim().toLowerCase()] = r.reason || "";
    const failedKeys = new Set();
    for (const idx of failedIdx || []) {
      for (const t of census.slice(idx * chunkSize, (idx + 1) * chunkSize)) failedKeys.add(t.title.trim().toLowerCase());
    }
    setRows(census.map(t => {
      const k = t.title.trim().toLowerCase();
      return { title: t.title, count: t.count, include: reasons[k] !== undefined,
               reason: reasons[k] !== undefined ? reasons[k] : (failedKeys.has(k) ? "Not analyzed — batch failed" : "") };
    }));
  };

  const adopt = (j) => {
    setJob(j);
    jobIdRef.current = j.job_id;
    if (j.brief && !brief) setBrief(j.brief);
    if (j.status === "running") {
      setPhase("scanning");
    } else if (j.status === "done") {
      buildRows(j.census || [], j.results || [], j.failed_idx || [], j.chunk_size || 250);
      setFailedInfo({ count: j.failed_chunks || 0, err: j.last_error || "" });
      setName(n => n || `AI audience — ${(j.brief || "").slice(0, 48).trim()}`);
      setPhase("review");
    }
  };

  useEffect(() => {
    api.lbJob("latest").then(j => {
      if (j.status === "running") adopt(j);
      else if (j.status === "done" && j.created_at && (Date.now() - new Date(j.created_at).getTime()) < 2 * 3600 * 1000 && (j.found_so_far || 0) > 0) adopt(j);
    }).catch(() => {});
    return () => clearInterval(pollRef.current);
  }, []);

  useEffect(() => {
    if (phase !== "scanning" || !jobIdRef.current) return;
    pollRef.current = setInterval(async () => {
      try {
        const j = await api.lbJob(jobIdRef.current);
        setJob(j);
        if (j.status === "done") {
          clearInterval(pollRef.current);
          adopt(j);
        }
      } catch (e) { /* transient poll errors are fine */ }
    }, 3000);
    return () => clearInterval(pollRef.current);
  }, [phase]);

  const scan = async () => {
    try {
      const r = await api.lbStart({ brief });
      jobIdRef.current = r.job_id;
      setJob({ status: "running", total_chunks: r.total_chunks, done_chunks: 0, found_so_far: 0 });
      setPhase("scanning");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const retryFailed = async () => {
    try {
      await api.lbJobRetry(jobIdRef.current);
      setPhase("scanning");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const toggle = (title) => setRows(rs => rs.map(r => r.title === title ? { ...r, include: !r.include } : r));

  const { included, excluded, estContacts } = useMemo(() => {
    const inc = rows.filter(r => r.include);
    return { included: inc, excluded: rows.filter(r => !r.include),
             estContacts: inc.reduce((s, r) => s + r.count, 0) };
  }, [rows]);

  const f = filter.trim().toLowerCase();
  const visIncluded = f ? included.filter(r => r.title.toLowerCase().includes(f)) : included;
  const visExcluded = f ? excluded.filter(r => r.title.toLowerCase().includes(f)) : excluded.slice(0, 400);

  const create = async () => {
    setCreating(true);
    try {
      const r = await api.lbCreate({ name, description: `AI-built audience — brief: ${brief.slice(0, 180)}`,
        titles: included.map(x => x.title) });
      toast.success(`"${r.name}" created with ${r.member_count} contacts across ${r.titles_used} job titles`);
      onSaved();
    } catch (e) {
      toast.error(fmtErr(e));
      setCreating(false);
    }
  };

  return (
    <Modal title="Build list with AI" subtitle="Describe the campaign — the AI reviews every job title in your contact base on the server, picks the relevant roles, and explains each choice. You can close this window and come back; the scan keeps running." onClose={onClose} wide>
      {phase === "brief" && (
        <div className="space-y-3">
          <Field label="What is this campaign about?">
            <textarea data-testid="ai-list-brief" value={brief} onChange={e => setBrief(e.target.value)} rows={5}
              placeholder="e.g. Promoting our new patient-recruitment feasibility service for rare-disease trials — aimed at people who plan or run clinical studies and struggle to find eligible patients…"
              className="modal-input w-full resize-y" />
          </Field>
          <button data-testid="ai-list-scan" onClick={scan} disabled={brief.trim().length < 15}
            className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-md bg-zinc-950 text-white hover:bg-zinc-800 disabled:opacity-50">
            <Sparkle size={14} /> Scan job titles with AI
          </button>
          <p className="text-[11px] text-zinc-400">Runs on the server in resilient batches — usually done in a few minutes, and safe to close this window.</p>
        </div>
      )}

      {phase === "scanning" && (
        <div className="py-8 text-center space-y-3" data-testid="ai-list-progress">
          <Sparkle size={22} className="mx-auto text-zinc-400 animate-pulse" />
          <p className="text-sm text-zinc-700">Reviewing your job titles against the brief…</p>
          <div className="max-w-sm mx-auto h-1.5 bg-zinc-100 rounded-full overflow-hidden">
            <div className="h-full bg-zinc-900 transition-all duration-500" style={{ width: `${job?.total_chunks ? Math.round((job.done_chunks || 0) / job.total_chunks * 100) : 0}%` }} />
          </div>
          <p className="text-xs text-zinc-500">
            Batch {Math.min((job?.done_chunks || 0) + 1, job?.total_chunks || 1)} of {job?.total_chunks || "…"}
            {(job?.found_so_far || 0) > 0 && <span className="text-emerald-700"> · {job.found_so_far} relevant roles found so far</span>}
            {(job?.failed_chunks || 0) > 0 && <span className="text-amber-600"> · {job.failed_chunks} batch(es) failed (retryable at the end)</span>}
          </p>
          <p className="text-[11px] text-zinc-400">Running on the server — closing this window won't stop it.</p>
        </div>
      )}

      {phase === "review" && (
        <div className="space-y-3" data-testid="ai-list-review">
          {failedInfo.count > 0 && (
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-2 flex items-center gap-2" data-testid="ai-list-failed-banner">
              <span className="min-w-0">{failedInfo.count} AI batch{failedInfo.count === 1 ? "" : "es"} failed after 4 attempts{failedInfo.err ? ` — ${failedInfo.err}` : ""}.</span>
              <button onClick={retryFailed} className="ml-auto shrink-0 underline font-semibold" data-testid="ai-list-retry-failed">Retry failed batches</button>
            </div>
          )}
          <div className="flex flex-wrap gap-1.5 items-center">
            <span className="text-[11px] font-semibold px-2 py-1 rounded-full bg-zinc-950 text-white" data-testid="ai-list-est">{estContacts.toLocaleString()} contacts selected</span>
            <span className="text-[11px] font-medium px-2 py-1 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700">{included.length} titles in</span>
            <span className="text-[11px] font-medium px-2 py-1 rounded-full border border-zinc-200 bg-zinc-50 text-zinc-600">{excluded.length} titles out</span>
            <input value={filter} onChange={e => setFilter(e.target.value)} placeholder="filter titles…" data-testid="ai-list-filter"
              className="h-7 w-40 rounded-md border border-zinc-200 px-2 text-xs focus:outline-none focus:border-zinc-400" />
            <button onClick={() => { setPhase("brief"); setJob(null); jobIdRef.current = null; }} className="text-[11px] text-zinc-500 underline ml-auto" data-testid="ai-list-back">Edit brief & rescan</button>
          </div>

          <div className="border border-zinc-200 rounded-md max-h-[380px] overflow-auto divide-y divide-zinc-100">
            {visIncluded.map((r) => <TitleRow key={r.title} r={r} onToggle={() => toggle(r.title)} />)}
            <div className="px-3 py-1.5 bg-zinc-50 text-[11px] font-medium text-zinc-500 sticky top-0" data-testid="ai-list-excluded-header">
              Excluded by AI ({excluded.length}) — tick to include{!f && excluded.length > 400 ? " · showing the 400 biggest, use the filter to find others" : ""}
            </div>
            {visExcluded.map((r) => <TitleRow key={r.title} r={r} dim onToggle={() => toggle(r.title)} />)}
          </div>

          <Field label="List name">
            <input data-testid="ai-list-name" value={name} onChange={e => setName(e.target.value)} className="modal-input w-full" />
          </Field>
        </div>
      )}

      <ModalFooter onClose={onClose}>
        <button data-testid="ai-list-create" disabled={phase !== "review" || estContacts === 0 || !name.trim() || creating} onClick={create}
          className="modal-btn-primary inline-flex items-center gap-1.5">
          <Sparkle size={14} /> {creating ? "Creating…" : `Create list (${estContacts.toLocaleString()} contacts)`}
        </button>
      </ModalFooter>
    </Modal>
  );
}

function TitleRow({ r, dim, onToggle }) {
  return (
    <label className={`flex items-start gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-zinc-50/70 ${dim ? "opacity-70" : ""}`}>
      <input type="checkbox" checked={r.include} onChange={onToggle} className="mt-1" />
      <span className={`min-w-0 ${r.include ? "text-zinc-900 font-medium" : "text-zinc-500"}`}>
        {r.title} <span className="text-zinc-400 font-normal">×{r.count}</span>
      </span>
      {r.reason && <span className="text-[11px] text-zinc-400 italic ml-auto text-right shrink-0 max-w-[45%] mt-0.5">{r.reason}</span>}
    </label>
  );
}
